import { type TemplateQualityGateSurface } from '@heidi/templates';
import { useCallback } from 'react';
import {
  resolveTemplateQualityGate,
  useTemplateQualityGate,
  type TemplateQualityGate,
} from './useTemplateQualityGate';

const CREATE_TEMPLATE_TOOL_NAME = 'create_template';

type ApprovalVerdict = 'approve' | 'deny';

/**
 * Applies the template quality attestation to agent-tool approvals that can
 * create a bespoke template. Denials and unrelated tools stay untouched.
 *
 * The attestation is a client-side risk control by design (see
 * useTemplateQualityGate — per-save, nothing persisted); the backend creates
 * the template on approval regardless. Any new surface that can approve
 * create_template — including a future mobile approval surface — must run this
 * same guard, or the control is silently bypassed.
 */
export const resolveTemplateCreationApprovalGuard = (
  gate: TemplateQualityGate,
  toolName: string,
  verdict: ApprovalVerdict
): Promise<boolean> =>
  verdict === 'approve' && toolName === CREATE_TEMPLATE_TOOL_NAME
    ? resolveTemplateQualityGate(gate, { kind: 'create' })
    : Promise.resolve(true);

/**
 * Shared preflight used by every frontend surface that can approve a tool.
 *
 * Each host passes its own surface: the desktop overlay is a separate WebView
 * with its own dialog host, so a regression there is invisible in the
 * main-window figures if the two are reported as one.
 */
export const useTemplateCreationApprovalGuard = (surface: TemplateQualityGateSurface) => {
  const templateQualityGate = useTemplateQualityGate(surface);

  return useCallback(
    (toolName: string, verdict: ApprovalVerdict) =>
      resolveTemplateCreationApprovalGuard(templateQualityGate, toolName, verdict),
    [templateQualityGate]
  );
};
